import {
  type CustomerQuote,
  EMPTY_ARRAY,
  type FixingIndex,
  QuoteStatusEnum,
  useObservableValue,
  useStaticSubscription,
  wsScanToMap,
} from '@talos/kyoko';
import { useMemo } from 'react';
import { map } from 'rxjs';

/**
 * Given the selected fixing index and an optional array of selected customer quotes, returns the
 * customer quotes that are set to be fixed given this selection. These returned customer quotes' RFQIDs are to be used
 * by you when sending out the apply-fixing REST requests.
 */
export const useCustomerQuotesToFix = ({
  selectedFixingIndices,
  selectedCustomerQuotes = EMPTY_ARRAY,
}: {
  selectedFixingIndices: FixingIndex[];
  selectedCustomerQuotes: CustomerQuote[] | undefined;
}) => {
  const { data: customerQuotesObs } = useStaticSubscription<CustomerQuote>({
    name: 'CustomerQuote',
    tag: 'FixQuotePriceDialog',
    Statuses: [QuoteStatusEnum.PendingFix],
  });

  const customerQuotes = useObservableValue(
    () =>
      customerQuotesObs.pipe(
        wsScanToMap({
          getUniqueKey: item => item.RFQID,
          newMapEachUpdate: true,
        }),
        map(map => Array.from(map.values()))
      ),
    [customerQuotesObs]
  );

  const selectedFixingIndicesSet = useMemo(
    () => new Set(selectedFixingIndices.map(fi => fi.Name)),
    [selectedFixingIndices]
  );

  const filteredCustomerQuotes = useMemo(() => {
    // If the user has not selected any CQs themselves, then we show them _all relevant CQs_, defined below
    if (selectedCustomerQuotes.length === 0) {
      const relevantQuotes =
        customerQuotes?.filter(quote => {
          return (
            quote.QuoteStatus === QuoteStatusEnum.PendingFix &&
            quote.fixingIndices?.some(fi => selectedFixingIndicesSet.has(fi))
          );
        }) ?? [];

      // Limit to one concrete fixing date; undated quotes are allowed to come along with that single date.
      const firstDefinedFixingDate = relevantQuotes.find(quote => quote.fixingDate != null)?.fixingDate;
      if (firstDefinedFixingDate == null) {
        return relevantQuotes;
      }
      return relevantQuotes.filter(quote => quote.fixingDate == null || quote.fixingDate === firstDefinedFixingDate);
    }

    // Otherwise, the user has selected some specific set of CQs, and we _only show those_
    const selectedRFQIDs = new Set(selectedCustomerQuotes.map(quote => quote.RFQID));
    return customerQuotes?.filter(quote => selectedRFQIDs.has(quote.RFQID)) ?? [];
  }, [customerQuotes, selectedCustomerQuotes, selectedFixingIndicesSet]);

  const fixingDate = useMemo(
    () => filteredCustomerQuotes.find(quote => quote.fixingDate != null)?.fixingDate,
    [filteredCustomerQuotes]
  );

  return { customerQuotesToFix: filteredCustomerQuotes, fixingDate };
};
